/* 术语词典：界面上悬浮解释的唯一数据源。
 *
 * 与 docs/术语表.md 同源。改这里之前先想清楚改的是"解释"还是"事实" ——
 * 涉及具体数值的（费率、阈值、上限）应当与 nexusbeta/console/profiles.py
 * 对齐，不要在这里写第二份真相。
 *
 * scope 的意义
 * ------------
 * common  行业通用，换个交易系统一样用得上，值得记
 * local   本系统 / 规则书 V1.2 自定义，搜不到，只在这个项目里成立
 *
 * 标出来是为了让人知道**哪些该去外面学、哪些只能问这里**。
 *
 * match 的意义
 * ------------
 * 自动匹配用的字面量。中文没有词边界，写得太泛会满屏下划线 ——
 * 所以宁可漏标也不要错标：像"价格""数量""金额"这类词一律不进 match。
 */

/** @typedef {{term:string, scope:'common'|'local', short:string, detail?:string, value?:string, match?:string[]}} Entry */

/** @type {Record<string, Entry>} */
export const GLOSSARY = {
  // --- 盘口与行情 ---------------------------------------------------------
  orderbook: {
    term: '盘口',
    scope: 'common',
    short: '交易所里所有未成交挂单的集合',
    detail:
      '分买盘（bids）与卖盘（asks）两侧，各自按价格排成若干"档"。盘口好不好，直接决定你的单子要付多少滑点。',
    match: ['盘口', '订单簿'],
  },
  bestBid: {
    term: '买一',
    scope: 'common',
    short: '买盘里出价最高的一档',
    detail: '你要马上卖出，就是卖给买一。这是当前"立刻能成交"的卖出价。',
    match: ['买一'],
  },
  bestAsk: {
    term: '卖一',
    scope: 'common',
    short: '卖盘里要价最低的一档',
    detail: '你要马上买入，就是从卖一买。这是当前"立刻能成交"的买入价。',
    match: ['卖一'],
  },
  mid: {
    term: '中间价',
    scope: 'common',
    short: '（买一 + 卖一）÷ 2',
    detail: '不是真实可成交价，只是一个参考中点，常用来计算相对价差。',
    match: ['中间价'],
  },
  spread: {
    term: '价差',
    scope: 'common',
    short: '卖一减买一，反映盘口好坏',
    detail:
      '本系统用相对价差：(卖一 − 买一) ÷ 中间价 × 10000，单位基点。价差越小，一买一卖越不吃亏。',
    value: '实测生产 BTC/USDT 0.0 bps；沙盒 141~2425 bps',
    match: ['价差'],
  },
  bps: {
    term: '基点',
    scope: 'common',
    short: '1 bps = 0.01% = 万分之一',
    detail:
      '金融里用它代替百分比，因为"涨了 0.5 个百分点"和"涨了 0.5%"容易混，而"涨了 50 个基点"没有歧义。',
    value: '10 bps = 0.1%｜35 bps = 0.35%（本系统往返成本）｜100 bps = 1%',
    match: ['基点', 'bps', 'BPS'],
  },
  depth: {
    term: '深度',
    scope: 'common',
    short: '盘口某一侧能吃下多少量',
    detail:
      '本系统按名义金额算：前 N 档的 Σ(价格 × 数量)。深度不够，你的单子会"吃穿盘口"——把价格自己打坏。',
    value: '沙盒实测：平掉 1.77 BTC 就把买一从 73,936 砸到 63,800',
    match: ['深度'],
  },
  slippage: {
    term: '滑点',
    scope: 'common',
    short: '委托价与实际成交价之差',
    detail:
      '想按 100 卖、实际成交在 99.8，那 0.2 就是滑点。注意：已实现盈亏不再单独扣滑点——它用的就是实际成交价，滑点已经在里面了，再扣一次是重复计算。',
    value: '本系统测算口径 0.05%',
    match: ['滑点'],
  },
  stale: {
    term: '断流',
    scope: 'local',
    short: '链路还连着，但数据不动了',
    detail:
      '最危险的故障不是断开（断开看得见），而是 socket 还活着、数据源已经挂了的"假活"。BTC 盘口静止 15 秒本身就说明出事了。',
    value: '生产 15 秒判定；沙盒关闭此判定（几十秒不动是常态）',
    match: ['断流', '行情过期'],
  },

  // --- 交易对与精度 -------------------------------------------------------
  symbol: {
    term: '交易对',
    scope: 'common',
    short: 'BASE/QUOTE，如 BTC/USDT',
    detail:
      'base（基础币）是被买卖的东西，quote（计价币）是用来标价的东西。内部一律用 BTC/USDT 格式，交易所原生写法只在适配器边界翻译。',
    match: ['交易对', '计价对'],
  },
  tick: {
    term: 'Tick',
    scope: 'common',
    short: '最小价格变动单位',
    detail: '价格只能是它的整数倍。BTC/USDT 的 tick 是 0.01，所以你报不出 50000.123。',
    match: ['tick', 'Tick', '最小价格变动'],
  },
  step: {
    term: 'Step',
    scope: 'common',
    short: '最小数量变动单位',
    detail: '数量只能是它的整数倍。本系统默认 0.00000001（1 聪）。',
    match: ['最小数量变动'],
  },
  notional: {
    term: '名义金额',
    scope: 'common',
    short: '价格 × 数量，即这笔单子值多少钱',
    detail: '交易所通常有最小下单金额（min_notional），低于它直接拒单。',
    match: ['名义金额'],
  },

  // --- 订单 ---------------------------------------------------------------
  limitOrder: {
    term: '限价单',
    scope: 'common',
    short: '你指定价格，保证价格不会更差',
    detail: '代价是可能不成交。本系统的受控平仓工具写死只能用限价单。',
    match: ['限价单', '限价'],
  },
  marketOrder: {
    term: '市价单',
    scope: 'common',
    short: '你指定数量，保证一定成交',
    detail:
      '代价是成交价没有下限。沙盒实测簿子第 12 档直接从 73,067 跳到 64,900，中间是空的——市价单会一路吃到那里。',
    match: ['市价单', '市价'],
  },
  maker: {
    term: 'Maker',
    scope: 'common',
    short: '挂单方，给市场提供流动性',
    detail: '你的单子挂在盘上等着成交。绝大多数交易所 maker 费率低于 taker。',
    match: ['Maker', 'maker', '挂单方'],
  },
  taker: {
    term: 'Taker',
    scope: 'common',
    short: '吃单方，消耗市场流动性',
    detail: '你的单子立刻吃掉别人的挂单。费率通常高于 maker。',
    match: ['Taker', 'taker', '吃单方'],
  },
  gtc: {
    term: 'GTC',
    scope: 'common',
    short: 'Good Till Cancel — 一直挂着',
    detail: '直到成交或你主动撤单为止。',
    match: ['GTC'],
  },
  ioc: {
    term: 'IOC',
    scope: 'common',
    short: 'Immediate Or Cancel — 能成多少成多少',
    detail: '立刻撮合，剩余部分立刻撤掉。接受部分成交。',
    match: ['IOC'],
  },
  fok: {
    term: 'FOK',
    scope: 'common',
    short: 'Fill Or Kill — 要么全成，要么全撤',
    detail: '不接受部分成交。与 IOC 的唯一区别就在这里。',
    match: ['FOK'],
  },
  orderUnknown: {
    term: 'UNKNOWN',
    scope: 'local',
    short: '下单结果未知——本地状态，不是交易所返回值',
    detail:
      '下单请求超时或网络中断、无法确定交易所是否收到单时使用。铁律：必须当作"可能已成交"处理，禁止直接重下——超时重下会导致重复建仓。',
    match: ['UNKNOWN'],
  },
  clientOrderId: {
    term: '客户端订单号',
    scope: 'common',
    short: '你自己生成的订单唯一标识',
    detail:
      '交易所原样带回，是幂等的关键：重试永远复用同一个，交易所只认一笔。格式为 <前缀><毫秒时间戳><序号><随机>，只含字母数字。',
    match: ['客户端订单号', 'clientOrderId'],
  },
  idempotent: {
    term: '幂等',
    scope: 'common',
    short: '同一操作执行一次和多次，结果相同',
    detail:
      '本系统的实现：每笔订单一个唯一客户端订单号，重试复用。撤单时订单不存在或已终态也视为成功。',
    match: ['幂等'],
  },

  // --- 账本与盈亏 ---------------------------------------------------------
  wma: {
    term: '移动加权平均',
    scope: 'common',
    short: '每次买入后重算持仓均价',
    detail: '新均价 =（原成本 + 本次买入金额）÷（原数量 + 本次数量）。另一种常见方法是 FIFO，本系统不用。',
    match: ['移动加权平均'],
  },
  avgEntry: {
    term: '持仓均价',
    scope: 'local',
    short: '纯价格成本，不含手续费',
    detail:
      '⚠ 判断赚没赚不能只看它——它会让你误以为刚过成本价就是赚。要用"含费成本"。',
    match: ['持仓均价', 'avg_entry_price'],
  },
  avgCostWithFee: {
    term: '含费成本',
    scope: 'local',
    short: '含买入手续费的每单位成本',
    detail: '这才是判断盈亏的基准。卖价高于它，才真的赚。',
    match: ['含费成本', 'avg_cost_with_fee'],
  },
  realized: {
    term: '已实现净盈亏',
    scope: 'common',
    short: '卖出成交后落袋的钱，扣完真实手续费',
    detail: '与"未实现"的区别：钱真的到手了，不会再随价格波动。',
    match: ['已实现净盈亏', '已实现盈亏', '已实现'],
  },
  unrealized: {
    term: '净浮盈亏',
    scope: 'common',
    short: '持仓期间的账面盈亏，钱没到手',
    detail:
      '公式：当前可成交卖出价值 − 买入成本 − 已发生买入手续费 − 预计卖出手续费 − 预计滑点。随价格实时波动。',
    match: ['净浮盈亏', '未实现盈亏', '浮盈', '浮亏'],
  },
  dailyPnl: {
    term: '当日净盈亏',
    scope: 'local',
    short: '已实现 + 净浮盈亏',
    detail:
      '风控三条线和保底棘轮判断的唯一依据。不能只看已实现（会忽略正在扩大的浮亏），也不能只看浮动（会忽略已落袋的利润）。',
    match: ['当日净盈亏', '当日累计净盈亏', 'daily_pnl_net'],
  },
  effFeeRate: {
    term: '有效费率',
    scope: 'local',
    short: '从历史成交反推出的真实费率',
    detail:
      'Σ实际手续费 ÷ Σ成交金额。比配置里写死的费率可靠——它就是这个账户在这个币对上实际被收的比例。',
    value: '本系统从 15 笔成交反推出卖出费率 0.15%',
    match: ['有效费率'],
  },
  opening: {
    term: '期初持仓',
    scope: 'local',
    short: '账本开始记录之前就存在的仓位',
    detail:
      '它没有成本可考，必须由人工显式声明均价，否则盈亏无从计算。账本绝不拿现价或 0 假装有成本。声明会重建账本。',
    match: ['期初持仓', '期初均价'],
  },
  untracked: {
    term: '未跟踪数量',
    scope: 'local',
    short: '交易所有、账本没有成本依据的持仓',
    detail: '出现它说明账本与交易所对不上，需要人工声明期初持仓来补齐。',
    match: ['未跟踪数量', 'untracked_qty'],
  },
  unfillable: {
    term: '吃不下的数量',
    scope: 'local',
    short: '按当前买盘深度平不掉的持仓量',
    detail: '大于 0 意味着你现在想跑也跑不掉。这是深度风险指标，不是账务指标。',
    match: ['吃不下的数量', 'unfillable_qty'],
  },
  grossValue: {
    term: '卖出毛额',
    scope: 'common',
    short: '买一价 × 持仓数量，未扣任何费用',
    detail: '它是浮盈计算的起点，不是你能拿到的钱——还要减手续费和滑点。',
    match: ['卖出毛额', '可成交卖出毛额'],
  },

  // --- 策略 ---------------------------------------------------------------
  refPrice: {
    term: '参考价',
    scope: 'local',
    short: '盘前生成计划时取的基准价，当天所有档位都从它推算',
    detail:
      '当日计划一旦生成就不再随行情变动，无论批准与否。参考价定死一次，三个档位跟着定死。',
    match: ['参考价'],
  },
  ladder: {
    term: '低吸档位',
    scope: 'local',
    short: '预设的分批买入价，从参考价往下排',
    detail: '跌得越深买得越多，摊低均价。备用档故意不给价——超出计划范围时必须有人拍板。',
    value: '参考价 −0.8% / −1.5% / −2.4%，每档 $20,000',
    match: ['低吸档位', '低吸'],
  },
  floorPrice: {
    term: '价格底线',
    scope: 'local',
    short: '跌破直接进入暂停',
    detail: '与低吸档位的区别：低吸是"机会"，底线是"止损"。跌到这里不再是多买一档的问题。',
    value: '参考价 −3.2%',
    match: ['价格底线'],
  },
  exitTier: {
    term: '止盈档位',
    scope: 'local',
    short: '按当日净盈亏分批卖出',
    detail: '一次全卖会错过后续上涨，一次不卖会把浮盈坐回去，所以分批。',
    value: '$1,800 卖 35%｜$3,200 卖 45%｜$5,000 卖 85%（留 15% 尾仓）',
    match: ['止盈档位', '止盈'],
  },
  breakeven: {
    term: '保本线',
    scope: 'local',
    short: '含费均价 × (1 + 往返成本)',
    detail:
      '低于它卖出就是亏的——哪怕价格比你买入价高。这是最容易被忽略的一条。',
    value: '含费均价上方 0.35%',
    match: ['保本线'],
  },
  grossMove: {
    term: '毛涨幅',
    scope: 'common',
    short: '价格涨了多少，未扣费用',
    detail: '"涨了 1% 就赚 1%"是错的——扣掉 0.35% 往返成本后实际只赚 0.65%。',
    match: ['毛涨幅'],
  },
  netProfit: {
    term: '净收益',
    scope: 'common',
    short: '实投资金 × (毛涨幅 − 往返成本)',
    detail: '一切目标反推都必须用净口径。本系统往返成本 0.35%。',
    match: ['净收益', '净空间'],
  },
  minNetProfit: {
    term: '单轮最低净利',
    scope: 'local',
    short: '低于这个数不值得出场',
    detail: '赚的还不够覆盖风险敞口暴露的时间。',
    value: '$500',
    match: ['单轮最低净利', 'min_net_profit'],
  },
  trailingDrawdown: {
    term: '价格回撤容忍',
    scope: 'local',
    short: '从最高价往下跌多少就动作',
    detail:
      '⚠ 这是价格回撤，不是利润回撤。保底棘轮要的是后者（当日净盈亏从峰值掉了多少），两者不能互相替代。',
    value: '0.75%',
    match: ['价格回撤', 'trailing_drawdown'],
  },
  ratchet: {
    term: '保底棘轮',
    scope: 'local',
    short: '当日利润的单向锁：只上不下',
    detail:
      '"棘轮"是机械术语，只能往一个方向转。当日净盈亏摸到 $1,000 后启动，锁住已赚到的、同时不封顶上方。触及保底线是止盈离场，不是风控事件——不进观察/暂停态，不需要人工解除。',
    value: '保底线 = max($800, 当日净峰值 × 75%)　【待建】',
    match: ['保底棘轮', '保底线'],
  },

  // --- 风控 ---------------------------------------------------------------
  stNormal: {
    term: '正常',
    scope: 'local',
    short: 'NORMAL — 正常买卖',
    detail: '五态状态机的起点。风控未触发任何阈值。',
  },
  stObserve: {
    term: '观察',
    scope: 'local',
    short: 'OBSERVE — 禁止新增买入，只监控与卖出',
    detail: '第一道风控闸门。仓位可以减，不可以加。',
    value: '触发：当日净亏损 $800',
  },
  stPaused: {
    term: '暂停',
    scope: 'local',
    short: 'PAUSED — 撤掉全部挂单，等待人工',
    detail: '行情断流、跌破价格底线也会直接进入这一态（fail closed）。',
    value: '触发：当日净亏损 $1,500',
  },
  stProtect: {
    term: '保护',
    scope: 'local',
    short: 'PROTECT — 锁定，停止主动交易',
    detail: '锁定态：进入后不得自行恢复，必须人工解除。',
    value: '触发：当日净亏损 $2,000',
  },
  stStopped: {
    term: '停止',
    scope: 'local',
    short: 'STOPPED — 完全停机',
    detail: '锁定态：只能人工解除。',
  },
  latching: {
    term: '锁定态',
    scope: 'common',
    short: '触发后自己保持，不会自动复位',
    detail:
      'Latching 是电路术语。本系统 PROTECT 与 STOPPED 属于此类，必须人工 release()。这些状态原本存在前端，刷新一次页面就被清掉——等于规则不存在，这是把状态挪到后端的首要理由。',
    match: ['锁定态'],
  },
  failClosed: {
    term: 'Fail Closed',
    scope: 'common',
    short: '出故障就停，宁可不做',
    detail:
      '与 Fail Open（出问题就放行，如门禁断电自动开门）相对。交易系统一律 fail closed——不确定的时候不动，比不确定的时候乱动便宜得多。',
    match: ['fail closed', 'Fail Closed', 'fail-closed'],
  },
  closeout: {
    term: '受控平仓',
    scope: 'local',
    short: '只能往一个方向走的减仓工具，不是通用下单口',
    detail:
      '只能：卖出、限价、不超过当前持仓、不低于底价。不能：买入、市价、超量卖出、追价、一次砸穿盘口。方向和类型是写死的常量——参数化就等于开了通用下单口。',
    match: ['受控平仓'],
  },
  slicing: {
    term: '分片',
    scope: 'local',
    short: '每片只卖当前盘口吃得下的量',
    detail:
      '剩余等下一轮，盘口需要时间补充。不预排后续分片——下一片的价格取决于成交后盘口怎么补，现在算不出来。宁可多点几次，也不给一个看起来精确的错数。',
    match: ['分片'],
  },
  tolerance: {
    term: '容忍滑点',
    scope: 'local',
    short: '单轮容忍：超过这个价位的档位不参与本轮',
    detail: '相对买一计算。上限硬编码 500 bps——再松就不叫"受控"了。',
    value: '生产默认 10 bps',
    match: ['容忍滑点'],
  },
  absFloor: {
    term: '绝对底价',
    scope: 'local',
    short: '总容忍：跌破就完全停手',
    detail: '与单轮容忍滑点是两道闸，一道管每片、一道管整体。',
    value: '生产默认 100 bps',
    match: ['绝对底价'],
  },
  checks: {
    term: '五项核验',
    scope: 'local',
    short: '盘前计划必须人工逐项勾选才能批准',
    detail:
      '①净空间覆盖双边手续费+滑点+安全边际 ②盘口价差与深度正常 ③无重大未决事件 ④接口余额挂单时钟正常 ⑤价格未接近日内压力位。改风险线会自动撤销批准——批准是对某一组具体档位的授权。',
    match: ['五项核验'],
  },
  needsCalibration: {
    term: '待标定',
    scope: 'local',
    short: '这个参数还没有实盘依据，先给的保守值',
    detail:
      '上线前必须按真实盘口重新定。把"我还不知道"写进代码而不是留在脑子里。',
    value: '当前标着的：单档金额、单日最大投入、行情过期阈值',
    match: ['待标定'],
  },

  // --- 接口与工程 ---------------------------------------------------------
  recvWindow: {
    term: 'recvWindow',
    scope: 'common',
    short: '交易所只接受时间戳偏差小于它的请求',
    detail:
      '防重放攻击。⚠ HashKey 的上限就是 5000 毫秒，填 10000 及以上一律返回 -1130——官方文档没写。这意味着"网络慢就放宽窗口"在 HashKey 上不成立，只能把本地时间戳算准。',
    match: ['recvWindow'],
  },
  clockSkew: {
    term: '时钟偏移',
    scope: 'common',
    short: '本地时钟与交易所服务器时钟的差',
    detail: '超过 recvWindow 就会签名失败。',
    match: ['时钟偏移'],
  },
  tokenBucket: {
    term: '令牌桶',
    scope: 'common',
    short: '限频算法：桶里按固定速率放令牌，取不到就等',
    detail:
      'HashKey 按 API Key 分类限频（查询 5 次/秒、下单 20 次/秒），所以按"桶名"隔离。宁可本地阻塞几十毫秒，也不要吃 429——退避窗口代价大得多，还会污染同一个 Key 的下单额度。',
    match: ['令牌桶', '限频'],
  },
  backoff: {
    term: '指数退避',
    scope: 'common',
    short: '失败后等 1 秒、2 秒、4 秒……而不是立刻重试',
    detail: 'HTTP 429（Too Many Requests）之后必须这么做。禁止并发重试风暴——同时重试会让情况更糟。',
    match: ['指数退避', '退避'],
  },
  adapter: {
    term: '适配器',
    scope: 'common',
    short: '让不同交易所的 API 对上层呈现同一套接口',
    detail:
      '交易所私有的字符串在各自的 mapping 模块里双向翻译，核心层（策略/风控/执行）永远看不到交易所方言。',
    match: ['适配器'],
  },
  profile: {
    term: '规则档案',
    scope: 'local',
    short: '按环境（沙盒/生产）分开存放的全部可调规则',
    detail:
      '策略参数两边一致，分开放是为了在沙盒试参数时不可能手滑改到生产那一份。数据质量类参数则必须分开——沙盒与生产差着数量级。',
    match: ['规则档案'],
  },
  roundTripCost: {
    term: '往返成本',
    scope: 'local',
    short: '买入费率 + 卖出费率 + 滑点',
    detail: '一买一卖走完全程要付的总代价。它同时是保本线的高度。',
    value: '0.35%（卖 0.15% 实测 + 买 0.15% 假设 + 滑点 0.05%）',
    match: ['往返成本'],
  },
}

/** 自动匹配用：[字面量, key]，长的排前面，避免"价差"抢走"价差上限"。 */
export const MATCHERS = Object.entries(GLOSSARY)
  .flatMap(([key, e]) => (e.match || []).map((m) => [m, key]))
  .sort((a, b) => b[0].length - a[0].length)

export function lookup(key) {
  return GLOSSARY[key] || null
}

export const SCOPE_LABEL = {
  common: '行业通用',
  local: '本系统',
}
